import { createModel } from "@rematch/core";
import DisplaySettingDefaults from "../defaults/DisplaySettingDefaults";
import { IViewSettingsState, DisplaySettings } from "../interfaces/states/IViewSettingsState";
import { ZOOM_MODE_MONTH, ITEM_DEFAULT_COLOR } from "../../gantt/helpers/Const";
import { NOTIFY, WARN } from "../../gantt/components/common/NotificationMessage";
import { setUserdisplaySettingsToNVSAsync, getUserDisplaySettingsFromNVSAsync } from "src/backend/services/NamedValueStorageService";
import { workflowStatesSelector } from "../selectors/selectors";
import i18n from "../../i18n";
import { iRootState, Dispatch } from "../store";

export default createModel()({
  state: {
    modalContents: null,
    modalStyles: {},
    displaySettings: DisplaySettingDefaults,
    zoomMode: ZOOM_MODE_MONTH,
    showTaskEditBox: false,
    showNewTaskBox: false,
    showConfigurationForm: false,
    showTokenForm: false,
    notificationMessage: null,
    notificationMessageLevel: null,
    showUIConfigForm: false,
  } as IViewSettingsState,
  reducers: {
    _setDisplaySettings: (state: IViewSettingsState, displaySettings: DisplaySettings): IViewSettingsState => {
      return { ...state, displaySettings: { ...state.displaySettings, ...displaySettings } };
    },
    setZoomMode: (state: IViewSettingsState, zoomMode: string): IViewSettingsState => {
      return { ...state, zoomMode: zoomMode };
    },
    showModal: (state: IViewSettingsState, contents, styles): IViewSettingsState => {
      return { ...state, modalContents: contents, modalStyles: styles };
    },
    hideModal: (state: IViewSettingsState): IViewSettingsState => {
      return { ...state, modalContents: null, modalStyles: {} };
    },
    toggleShowEditBox: (state: IViewSettingsState) => {
      return { ...state, showTaskEditBox: !state.showTaskEditBox, showNewTaskBox: false };
    },
    showEditBox: (state: IViewSettingsState) => {
      return { ...state, showTaskEditBox: true, showNewTaskBox: false };
    },
    toggleShowAddWorkBox: (state: IViewSettingsState) => {
      return { ...state, showNewTaskBox: !state.showNewTaskBox, showTaskEditBox: false };
    },
    toggleShowConfigurationForm: (state: IViewSettingsState) => {
      return { ...state, showConfigurationForm: !state.showConfigurationForm };
    },
    toggleShowTokenForm: (state: IViewSettingsState) => {
      return { ...state, showTokenForm: !state.showTokenForm };
    },
    toggleShowUIConfigForm: (state: IViewSettingsState) => {
      return { ...state, showUIConfigForm: !state.showUIConfigForm };
    },
    showMessage: (state: IViewSettingsState, message: string, messageLevel: number = NOTIFY): IViewSettingsState => {
      if (messageLevel === undefined) {
        messageLevel = NOTIFY;
      }
      let messageId = state.notificationMessageId + 1;
      if(messageId === undefined || isNaN(messageId)) {
        messageId = 1;
      }
      return { ...state, notificationMessage: message, notificationMessageLevel: messageLevel, notificationMessageId: messageId };
    },
    hideMessage: (state: IViewSettingsState): IViewSettingsState => {
      return { ...state, notificationMessage: null, notificationMessageLevel: null, notificationMessageId: 0 };
    },
    setDayClicked: (state: IViewSettingsState, payload: Date): IViewSettingsState => {
      return {
        ...state,
        dayClicked: payload
      };
    },
  },
  effects: (dispatch0) => ({
    loadDisplaySettingsAsync: async (_: null, rootState: iRootState) => {
      const dispatch = dispatch0 as Dispatch;
      let displaySettings = { ...DisplaySettingDefaults };
      try {
        const displaySettingsFromNvs = await getUserDisplaySettingsFromNVSAsync(
          rootState.common.configuration.baseConfiguration,
          rootState.common.configuration.userId
        );
        displaySettings = { ...DisplaySettingDefaults, ...displaySettingsFromNvs };
      } catch (err) {
        if (err?.response?.status && err?.response?.status !== 404) {
          dispatch.UI.showMessage(i18n.t("error.nvsloadfailed", {err: err}), WARN);
        }
        // Received a 404, because user simply hasn't saved settings to NVS yet.
      }

      //Remove time from dateFormat since we now have separate timeFormat
      if(displaySettings.dateFormat.indexOf("HH") > 0) {
        displaySettings.dateFormat = "P"; //long localized date
      }

      // Set color options available to settings editor
      const workflowStates = workflowStatesSelector(rootState as never);
      const defaultStateInfo = workflowStates.reduce((acc, workflowState, i) => {
        const color = i < displaySettings?.colorOptions?.length ? displaySettings.colorOptions?.[i] : ITEM_DEFAULT_COLOR;
        acc[workflowState.ID] = { name: `${workflowState.WorkflowName}: ${workflowState.Name}`, color: color };
        return acc;
      }, {});
      displaySettings.stateInfo = { ...defaultStateInfo, ...displaySettings.stateInfo };

      dispatch.UI._setDisplaySettings(displaySettings);
    },
    saveDisplaySettingsAsync: async (displaySettings: DisplaySettings, rootState: iRootState) => {
      const dispatch = dispatch0 as Dispatch;
      dispatch.UI._setDisplaySettings(displaySettings);
      try {
        await setUserdisplaySettingsToNVSAsync(
          rootState.common.configuration.baseConfiguration,
          rootState.common.configuration.userId,
          displaySettings
        );
      } catch (err) {
        dispatch.UI.showMessage(i18n.t("error.nvssavefailed", {err: err}), WARN);
      }
    },
  }),
});
